import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCheck, Layers, Printer, Sticker } from 'lucide-react';
import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { PageHeader } from '../../components/common/PageHeader';
import { Panel } from '../../components/common/Panel';
import { StatusBadge } from '../../components/common/StatusBadge';
import { NativeSelect } from '../../components/common/NativeSelect';
import { CngQrSticker } from '../../components/stickers/CngQrSticker';
import { usePrototype } from '../../contexts/PrototypeContext';
import { trafficDivisions } from '../../data/cngs';
import { toBn } from '../../utils/bn';
interface PrintRow {
  id: string;
  reg: string;
  qrId: string;
  owner: string;
  division: string;
  approved: string;
  status: 'তৈরিকৃত' | 'প্রিন্টকৃত';
  real: boolean;
}
const extras: PrintRow[] = [{
  id: 'x1',
  reg: 'ঢাকা-থ-১৬-০৪৬০',
  qrId: 'CNG-016460',
  owner: 'মোঃ আব্দুর রাজ্জাক',
  division: 'মিরপুর',
  approved: '১১ মার্চ ২০২৬',
  status: 'তৈরিকৃত',
  real: false
}, {
  id: 'x2',
  reg: 'ঢাকা-থ-১৬-০৪৬৩',
  qrId: 'CNG-016463',
  owner: 'মোসাঃ রোকসানা খাতুন',
  division: 'মিরপুর',
  approved: '১১ মার্চ ২০২৬',
  status: 'তৈরিকৃত',
  real: false
}, {
  id: 'x3',
  reg: 'ঢাকা-থ-১২-৭৭০১',
  qrId: 'CNG-012701',
  owner: 'মোঃ লিটন মিয়া',
  division: 'গুলশান',
  approved: '১১ মার্চ ২০২৬',
  status: 'তৈরিকৃত',
  real: false
}, {
  id: 'x4',
  reg: 'ঢাকা-থ-১৩-২০৮৮',
  qrId: 'CNG-013088',
  owner: 'মোঃ হুমায়ুন কবির',
  division: 'তেজগাঁও',
  approved: '১০ মার্চ ২০২৬',
  status: 'তৈরিকৃত',
  real: false
}, {
  id: 'x5',
  reg: 'ঢাকা-থ-১৪-৫৫১২',
  qrId: 'CNG-014512',
  owner: 'মোঃ আজাদ রহমান',
  division: 'লালবাগ',
  approved: '১০ মার্চ ২০২৬',
  status: 'তৈরিকৃত',
  real: false
}, {
  id: 'x6',
  reg: 'ঢাকা-থ-১১-৮৮২০',
  qrId: 'CNG-011820',
  owner: 'মোঃ সোহরাব হোসেন',
  division: 'রমনা',
  approved: '১০ মার্চ ২০২৬',
  status: 'তৈরিকৃত',
  real: false
}, {
  id: 'x7',
  reg: 'ঢাকা-থ-১৫-৩৩০৯',
  qrId: 'CNG-015309',
  owner: 'মোঃ এনামুল হক',
  division: 'মতিঝিল',
  approved: '০৯ মার্চ ২০২৬',
  status: 'তৈরিকৃত',
  real: false
}];
export function BulkPrinting() {
  const navigate = useNavigate();
  const {
    cngList,
    setStage
  } = usePrototype();
  const [division, setDivision] = useState('সকল বিভাগ');
  const [date, setDate] = useState('সকল তারিখ');
  const [stickerStatus, setStickerStatus] = useState('প্রিন্টের অপেক্ষায়');
  const [selected, setSelected] = useState<string[]>([]);
  const [batch, setBatch] = useState<string | null>(null);
  const [printedExtras, setPrintedExtras] = useState<string[]>([]);
  const rows: PrintRow[] = useMemo(() => {
    const real: PrintRow[] = cngList.filter(c => c.stage === 5 || c.stage === 6).map(c => ({
      id: c.id,
      reg: c.reg,
      qrId: c.qrId,
      owner: c.owner.name,
      division: c.division,
      approved: c.approvedOn ?? '১২ মার্চ ২০২৬',
      status: c.stage === 5 ? 'তৈরিকৃত' : 'প্রিন্টকৃত',
      real: true
    }));
    const ex = extras.map(e => printedExtras.includes(e.id) ? {
      ...e,
      status: 'প্রিন্টকৃত' as const
    } : e);
    return [...real, ...ex].filter(r => {
      if (division !== 'সকল বিভাগ' && r.division !== division) return false;
      if (date !== 'সকল তারিখ' && r.approved !== date) return false;
      if (stickerStatus === 'প্রিন্টের অপেক্ষায়' && r.status !== 'তৈরিকৃত') return false;
      if (stickerStatus === 'প্রিন্টকৃত' && r.status !== 'প্রিন্টকৃত') return false;
      return true;
    });
  }, [cngList, division, date, stickerStatus, printedExtras]);
  const allSelected = rows.length > 0 && rows.every(r => selected.includes(r.id));
  const sheet = rows.filter(r => selected.includes(r.id));
  const markPrinted = () => {
    sheet.forEach(r => r.real ? setStage(r.id, 6) : null);
    setPrintedExtras(p => [...p, ...sheet.filter(r => !r.real).map(r => r.id)]);
    toast.success(`${toBn(sheet.length)}টি স্টিকার প্রিন্টকৃত হিসেবে চিহ্নিত — ইস্যুর জন্য প্রস্তুত`);
    const firstReal = sheet.find(r => r.real);
    setSelected([]);
    setBatch(null);
    if (firstReal) navigate(`/admin/issue?id=${firstReal.id}`);
  };
  return <div data-id="element-930" data-mp="1kbjg3ageuf4k">
      <PageHeader title="QR স্টিকার প্রিন্ট ব্যবস্থাপনা" subtitle="তৈরিকৃত স্টিকার নির্বাচন করে A4 শিটে ব্যাচ প্রিন্ট" actions={<>
            <Button variant="outline" disabled={sheet.length === 0} onClick={() => {
        const b = `BATCH-2026-0312-${String(Math.floor(Math.random() * 90) + 10)}`;
        setBatch(b);
        toast.success(`ব্যাচ তৈরি: ${b}`);
      }} data-id="element-932" data-mp="1ak6pem6clv04"><Layers className="h-4 w-4" data-id="element-933" data-mp="239vrz3ypi33u" />Generate Batch</Button>
            <Button variant="outline" disabled={!batch} onClick={() => {
        toast.success('প্রিন্টারে পাঠানো হয়েছে — HP LaserJet (স্টিকার সেল)');
      }} data-id="element-934" data-mp="1p9upxl6pgpms"><Printer className="h-4 w-4" data-id="element-935" data-mp="1taocob8evye3" />Print Stickers</Button>
            <Button disabled={!batch} onClick={markPrinted} data-id="element-936" data-mp="dup8fb1x0mj4"><CheckCheck className="h-4 w-4" data-id="element-937" data-mp="dtwb10ouj9dn" />Mark as Printed</Button>
          </>} data-id="element-931" data-mp="1kccf1p94o0c5" />
      <div className="grid gap-4 2xl:grid-cols-[1fr_520px] xl:grid-cols-[1fr_440px]" data-id="element-938" data-mp="hxc8u0cnjfrx">
        <Panel bodyClassName="p-0" data-id="element-939" data-mp="abytljmos7zy">
          <div className="grid gap-3 border-b border-border p-4 md:grid-cols-3" data-id="element-940" data-mp="aax7mk44u13g">
            <NativeSelect id="bp-d" label="Traffic Division" options={['সকল বিভাগ', ...trafficDivisions]} value={division} onChange={e => setDivision(e.target.value)} data-id="element-941" data-mp="5cvo5y2snzrg" />
            <NativeSelect id="bp-a" label="Approval Date" options={['সকল তারিখ', '১২ মার্চ ২০২৬', '১১ মার্চ ২০২৬', '১০ মার্চ ২০২৬', '০৯ মার্চ ২০২৬']} value={date} onChange={e => setDate(e.target.value)} data-id="element-942" data-mp="fbbdbvi9gyyb" />
            <NativeSelect id="bp-s" label="Sticker Status" options={['প্রিন্টের অপেক্ষায়', 'প্রিন্টকৃত', 'সকল']} value={stickerStatus} onChange={e => setStickerStatus(e.target.value)} data-id="element-943" data-mp="3itcvoylcts2q" />
          </div>
          <div className="flex items-center justify-between border-b border-border bg-slate-50 px-4 py-2 text-xs" data-id="element-944" data-mp="3sppxkjr1zc8k">
            <label className="flex items-center gap-2 font-medium text-navy-900" data-id="element-945" data-mp="34zvjxzywtjn9">
              <Checkbox checked={allSelected} onCheckedChange={v => setSelected(v ? rows.map(r => r.id) : [])} aria-label="Select All" data-id="element-946" data-mp="3010uwgacmyup" />
              Select All — সব নির্বাচন
            </label>
            <span className="text-slate-500" data-id="element-947" data-mp="3tqrhujm961ba">{toBn(selected.length)}টি নির্বাচিত{batch && <> • <b className="font-mono text-navy-800" data-id="element-948" data-mp="2cn6gi1o4nuq">{batch}</b></>}</span>
          </div>
          <table className="w-full text-sm" data-id="element-949" data-mp="7byn7r21btcb">
            <tbody data-id="element-950" data-mp="7bqbi2hneu2p">
              {rows.map(r => <tr key={r.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-id="element-951" data-mp="36rmzu5qdsojy">
                  <td className="w-10 px-4 py-3" data-id="element-952" data-mp="3asf74tfp0jdl">
                    <Checkbox checked={selected.includes(r.id)} onCheckedChange={v => setSelected(s => v ? [...s, r.id] : s.filter(x => x !== r.id))} aria-label={`${r.reg} নির্বাচন`} data-id="element-953" data-mp="3at8ik7pljx22" />
                  </td>
                  <td className="py-3" data-id="element-954" data-mp="3kp1txnrkxki9">
                    <p className="font-semibold text-navy-900" data-id="element-955" data-mp="v2ly8l78yz33">{r.reg}</p>
                    <p className="font-mono text-[11px] text-slate-400" data-id="element-956" data-mp="100x64qdzezio">{r.qrId}</p>
                  </td>
                  <td className="py-3 text-slate-600" data-id="element-957" data-mp="2kiokm3yu7xoa">{r.owner}</td>
                  <td className="py-3 text-slate-600" data-id="element-958" data-mp="2kl0yo61h5nw2">ট্রাফিক-{r.division}</td>
                  <td className="py-3 text-xs text-slate-500" data-id="element-959" data-mp="2fkdads4mrl5j">{r.approved}</td>
                  <td className="px-4 py-3" data-id="element-960" data-mp="2as3bhmljfar4"><StatusBadge tone={r.status === 'তৈরিকৃত' ? 'orange' : 'blue'} data-id="element-961" data-mp="2ark5n495wctg">{r.status}</StatusBadge></td>
                </tr>)}
              {rows.length === 0 && <tr data-id="element-962" data-mp="21sqalibhbui0"><td className="px-4 py-10 text-center text-sm text-slate-500" data-id="element-963" data-mp="21thx83n3mhw5">এই ফিল্টারে কোনো স্টিকার নেই</td></tr>}
            </tbody>
          </table>
        </Panel>

        <Panel title="A4 স্টিকার শিট প্রিভিউ" subtitle="২১০ × ২৯৭ মিমি • প্রতি শিটে ৬টি গাড়ির স্টিকার" data-id="element-964" data-mp="1ruahj9sw6b5v">
          <div className="flex justify-center bg-slate-200 p-4" data-id="element-965" data-mp="1mvfsvr88tsdu">
            <div className="grid aspect-[210/297] w-full max-w-[420px] grid-cols-3 content-start gap-3 bg-white p-4 shadow-md" data-id="element-966" data-mp="1mxaez8v93qji">
              {sheet.slice(0, 6).map(r => <div key={r.id} className="flex justify-center [&>div]:origin-top [&>div]:scale-[0.78]" data-id="element-967" data-mp="3h9qgxxh0kiik">
                  <CngQrSticker qrId={r.qrId} reg={r.reg} size="sm" data-id="element-968" data-mp="2q4eek3r2zxq9" />
                </div>)}
              {sheet.length === 0 && <div className="col-span-3 flex h-full min-h-[300px] flex-col items-center justify-center text-center text-sm text-slate-400" data-id="element-969" data-mp="2hbsvsnng9r1f">
                  <Sticker className="mb-2 h-10 w-10" data-id="element-970" data-mp="2lbl9tfvm4pe6" />
                  তালিকা থেকে স্টিকার নির্বাচন করুন
                </div>}
            </div>
          </div>
          {sheet.length > 6 && <p className="mt-2 text-center text-xs text-slate-500" data-id="element-971" data-mp="2cd9e4zfw31ev">মোট {toBn(Math.ceil(sheet.length / 6))}টি শিট প্রয়োজন</p>}
        </Panel>
      </div>
    </div>;
}